<template>
  <div class="canonical-update-review">
    <p v-if="store.summaryText" class="canonical-update-review__summary">{{ store.summaryText }}</p>

    <ul
      v-if="topLevelErrors.length > 0"
      class="canonical-update-review__warning"
      tabindex="0"
      :aria-label="store.labels.topLevelErrors"
    >
      <li v-for="(error, index) in topLevelErrors" :key="index">{{ error }}</li>
    </ul>

    <!-- Above the table: a confirm failure must be visible without scrolling past
         a long review. -->
    <div v-if="store.errorMessage" class="canonical-update-review__error" role="alert">
      {{ store.errorMessage }}
    </div>

    <p v-if="store.previewData" class="canonical-update-review__note">{{ store.labels.rejectedNote }}</p>

    <!-- What unchecking means: left for a later review, or rejected once the actions bar's toggle says so. -->
    <p v-if="hasActionableRows" class="canonical-update-review__note">{{ store.uncheckedRowsNote }}</p>

    <p v-if="showEmptyState" class="canonical-update-review__empty">{{ store.labels.emptyState }}</p>

    <!-- Rows exist but none are applyable (all unchanged/skipped/error), so the
         disabled Apply button needs an explanation. -->
    <p v-if="showNoActionableNote" class="canonical-update-review__empty">{{ store.labels.noActionableNote }}</p>

    <!-- Where the canonical list comes from and when it was last applied. -->
    <p v-if="store.labels.sourceNote" class="canonical-update-review__source">{{ store.labels.sourceNote }}</p>

    <!-- The one scroll area of the review: both tables scroll together, each header pinning in turn,
         while the modal body stays put (the app turns the body's own scrolling off once rows exist). -->
    <div class="canonical-update-review__tables">
      <section v-if="rows.length > 0" class="canonical-update-review__review">
        <!-- With nothing to apply, every row is here for its notice, and the heading says so. -->
        <h3 v-if="noticesOnly" class="canonical-update-review__heading">{{ store.labels.noticesHeading }}</h3>

        <ul class="canonical-update-review__legend" :aria-label="store.labels.legendHeading">
          <li v-for="entry in legendEntries" :key="entry.key" class="canonical-update-review__legend-item">
            <RowStatusIcon :status="entry.status" />
            {{ entry.label }}
          </li>
        </ul>

        <div class="preview-table preview-table--review">
          <table class="preview-table__table">
            <thead>
              <tr>
                <!-- Without an actionable row there is nothing to select, so the column is omitted. -->
                <th v-if="hasActionableRows" class="preview-table__select">
                  <ReviewSelectionHeaderCell
                    :all-selected="store.allRowsSelected"
                    :indeterminate="store.selectionIndeterminate"
                    :label="selectionLabels.selectAll"
                    :test-id="TestId.canonicalUpdate.selectAll"
                    @toggle-all="store.toggleAllRows"
                  />
                </th>
                <th class="preview-table__status">
                  <!-- The column shows only status icons; the label is for assistive tech. -->
                  <span class="preview-table__sr-only">{{ columnLabels.status }}</span>
                </th>
                <th>{{ columnLabels.name }}</th>
                <th>{{ columnLabels.type }}</th>
                <th>{{ columnLabels.changes }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in visibleItems"
                :key="`${index}:${rowKey(row)}`"
                :data-testid="TestId.canonicalUpdate.previewRow"
              >
                <td v-if="hasActionableRows" class="preview-table__select">
                  <!-- Error rows are display-only: no checkbox, just an empty cell. -->
                  <ReviewSelectionCell
                    v-if="isActionableRow(row)"
                    :checked="store.isRowSelected(rowIdentity(row))"
                    :label="includeRowLabel(row)"
                    :test-id="TestId.canonicalUpdate.rowCheckbox"
                    @toggle="handleRowToggle(row)"
                  />
                </td>
                <td class="preview-table__status">
                  <RowStatusIcon :status="iconStatus(row)" :label="statusLabel(row)" />
                </td>
                <td>{{ row.name }}</td>
                <td>{{ row.type }}</td>
                <td :data-testid="TestId.canonicalUpdate.changesCell">
                  <div class="preview-table__changes">
                    <RowStatusIcon v-if="rowWarnings(row)" status="warning" :label="rowWarnings(row)" />
                    <ChangeSetDiff :changeset="row.changes" :labels="diffLabels" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <TableFooter
            v-if="showPager"
            class="preview-table__footer"
            variant="steps"
            :page="page"
            :page-count="pageCount"
            :per-page="perPage"
            :per-page-choices="perPageChoices"
            :is-first-page="isFirstPage"
            :is-last-page="isLastPage"
            @page-change="handlePageChange"
          />
        </div>
      </section>

      <!-- Skipped rows are listed apart, with the reason in text: nothing about them is decided. -->
      <section v-if="skippedRows.length > 0" class="canonical-update-review__skipped">
        <h3 class="canonical-update-review__heading">{{ store.labels.skippedHeading }}</h3>
        <div class="preview-table preview-table--skipped">
          <table class="preview-table__table">
            <thead>
              <tr>
                <th>{{ columnLabels.name }}</th>
                <th>{{ columnLabels.type }}</th>
                <th>{{ columnLabels.reason }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in skippedRows"
                :key="`skipped:${index}:${rowKey(row)}`"
                :data-testid="TestId.canonicalUpdate.skippedRow"
              >
                <td>{{ row.name }}</td>
                <td>{{ row.type }}</td>
                <td>{{ skipReason(row) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { TestId } from "#/test-ids";
import ChangeSetDiff from "#ce/apps/SpAdmin/shared/components/ChangeSetDiff.ce.vue";
import ReviewSelectionCell from "#ce/apps/SpAdmin/shared/components/ReviewSelectionCell.ce.vue";
import ReviewSelectionHeaderCell from "#ce/apps/SpAdmin/shared/components/ReviewSelectionHeaderCell.ce.vue";
import RowStatusIcon from "#ce/apps/SpAdmin/shared/components/RowStatusIcon.ce.vue";
import TableFooter from "#ce/components/SpTable/components/TableFooter.ce.vue";
import { useTablePagination } from "#ce/components/SpTable/composables/useTablePagination";
import {
  isActionableRow,
  rowIdentity,
  rowKey,
  useCanonicalUpdateStore,
} from "#ce/apps/SpAdmin/SpCanonicalUpdateApp/store/canonical-update";

const PER_PAGE_OPTIONS = [50, 100, 250];

const store = useCanonicalUpdateStore();

// RowStatusIcon's "existing" variant is the update glyph, so "updated" rows map onto it.
// "unchanged" rows only reach the table when they carry warnings worth surfacing; skipped
// rows never do, they have a table of their own.
const ICON_STATUSES = {
  new: "new",
  updated: "existing",
  removed: "removed",
  unchanged: "unchanged",
};

// The legend's order, keyed like the status labels. "error" and "warning" have no
// ICON_STATUSES entry: the cross of a failed row and the triangle of a notice.
const LEGEND_ORDER = ["new", "updated", "removed", "unchanged", "error", "warning"];

const rows = computed(() => store.reviewRows);
const skippedRows = computed(() => store.skippedRows);
const topLevelErrors = computed(() => store.previewData?.errors ?? []);
const columnLabels = computed(() => store.labels.columns ?? {});
const selectionLabels = computed(() => store.labels.selection ?? {});

const hasActionableRows = computed(() => store.actionableRows.length > 0);
// The rejected-terms list's rows are work without a checkbox, so they keep the plain table.
const noticesOnly = computed(() => !store.applyHasWork);

function handleRowToggle(row) {
  store.toggleRow(rowIdentity(row));
}
// Top-level errors (malformed sheet, failed start) replace the empty state — zero rows
// alongside an error list must not read as "everything is in sync".
const showEmptyState = computed(
  () => Boolean(store.previewData) && store.rows.length === 0 && topLevelErrors.value.length === 0,
);
// Rows the rejected-terms list applies count as work, so the note stays away while any remain.
const showNoActionableNote = computed(() => store.rows.length > 0 && !store.applyHasWork);
const diffLabels = computed(() => ({
  attributes: store.labels.attributes,
  removedValue: store.labels.removedValue,
}));

// One entry per glyph the table shows, across every page, named by its status label.
const legendEntries = computed(() => {
  const present = new Set();

  for (const row of rows.value) {
    present.add(ICON_STATUSES[row.status] ? row.status : "error");

    if (rowWarnings(row)) {
      present.add("warning");
    }
  }

  return LEGEND_ORDER.filter((key) => present.has(key)).map((key) => ({
    key,
    status: ICON_STATUSES[key] ?? key,
    label: store.statusLabels[key] ?? "",
  }));
});

// Only the rendering is paged. The summary sentence and the actionable-row note count every row.
const requestedPage = ref(1);
const requestedPerPage = ref(PER_PAGE_OPTIONS[1]);
const { page, perPage, pageCount, isPaginated, isFirstPage, isLastPage, visibleItems, perPageChoices } =
  useTablePagination({
    items: rows,
    page: requestedPage,
    perPage: requestedPerPage,
    clientPaginate: true,
    perPageOptions: PER_PAGE_OPTIONS,
  });
// Hidden while every page size would render the same rows.
const showPager = computed(() => isPaginated.value && rows.value.length > PER_PAGE_OPTIONS[0]);

// A new preview replaces the rows, so the view returns to its first page.
function resetPage() {
  requestedPage.value = 1;
}

watch(rows, resetPage);

function handlePageChange({ page: nextPage, perPage: nextPerPage }) {
  requestedPerPage.value = nextPerPage;
  requestedPage.value = nextPage;
}

function includeRowLabel(row) {
  return String(selectionLabels.value.includeRow ?? "").replaceAll("%{name}", row.name);
}

function iconStatus(row) {
  return ICON_STATUSES[row.status] ?? "error";
}

// Any row can carry `errors`. On error rows they are the failure, the explanation of the
// status itself, so they belong in the status tooltip. On every other status they are
// nonfatal warnings (e.g. a specialty name that only resolved via mapping): the status icon
// shows the real outcome and the warnings surface as a triangle in the Changes cell.
function errorsExplainStatus(row) {
  return !(row.status in ICON_STATUSES);
}

// The skip reason the backend recorded, or the plain status label where it recorded none.
function skipReason(row) {
  return row.errors?.join(", ") || store.statusLabels.skipped || "";
}

function statusLabel(row) {
  const label = store.statusLabels[row.status] ?? "";

  if (!errorsExplainStatus(row)) {
    return label;
  }

  const reasons = row.errors?.join(", ") ?? "";

  if (label && reasons) {
    return `${label} — ${reasons}`;
  }

  return label || reasons;
}

function rowWarnings(row) {
  if (errorsExplainStatus(row)) {
    return "";
  }

  return row.errors?.join(", ") ?? "";
}
</script>

<style scoped lang="scss">
.canonical-update-review {
  display: flex;
  flex-direction: column;
  gap: var(--sp-ref-scale-16);
  // Fill the modal body so the preview table gets a real height limit instead
  // of making the whole modal grow.
  height: 100%;
  min-height: 0;

  &__summary,
  &__empty,
  &__note,
  &__source {
    font-size: var(--sp-sys-typescale-body-font-size);
    margin: 0;
  }

  &__note,
  &__source {
    font-size: var(--sp-sys-typescale-small-font-size);
    color: var(--sp-sys-color-on-surface-variant);
  }

  &__warning {
    // These are sheet-level errors: use the error token (the warning token's
    // yellow fails contrast on the light surface).
    color: var(--sp-sys-color-error);
    font-size: var(--sp-sys-typescale-small-font-size);
    flex: 0 1 auto;
    max-height: 12rem;
    min-height: var(--sp-ref-scale-48);
    margin: 0;
    overflow-y: auto;
  }

  &__error {
    color: var(--sp-sys-color-error);
    font-size: var(--sp-sys-typescale-small-font-size);
  }

  &__review,
  &__skipped {
    display: flex;
    flex-direction: column;
    gap: var(--sp-ref-scale-4);
  }

  &__heading {
    margin: 0;
    font-size: var(--sp-sys-typescale-body-font-size);
    font-weight: var(--sp-ref-font-weight-normal);
    color: var(--sp-sys-color-on-surface-variant);
  }

  &__legend {
    display: flex;
    flex-wrap: wrap;
    gap: var(--sp-ref-scale-4) var(--sp-ref-scale-16);
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: var(--sp-sys-typescale-small-font-size);
    color: var(--sp-sys-color-on-surface-variant);
  }

  &__legend-item {
    display: inline-flex;
    align-items: center;
    gap: var(--sp-ref-scale-4);
  }

  // The one scroll area: takes the room the notes leave in the modal body, so the summary and
  // notes stay in view while the tables scroll together.
  &__tables {
    display: flex;
    flex-direction: column;
    gap: var(--sp-ref-scale-16);
    flex: 1 1 auto;
    min-height: var(--sp-ref-scale-48);
    overflow-y: auto;
  }
}

.preview-table {
  // The pager reads SpTable's header-colour alias through inheritance.
  --table-header-color: var(--sp-comp-table-header-color, var(--sp-sys-color-on-surface-variant));

  // Sized to its rows: the modal's content region is the one scroll area, so the two tables
  // scroll together and each header pins in turn.
  border: var(--sp-sys-border-width-default) solid var(--sp-sys-color-outline-variant);
  border-radius: var(--sp-ref-scale-4);

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--sp-sys-typescale-small-font-size);

    th,
    td {
      padding: var(--sp-ref-scale-8);
      text-align: left;
      border-bottom: var(--sp-sys-border-width-default) solid var(--sp-sys-color-outline-variant);
    }

    th {
      font-size: var(--sp-ref-font-size-small);
      font-weight: var(--sp-ref-font-weight-normal);
      letter-spacing: 0.5px;
      color: var(--sp-sys-color-on-surface-variant);
      background-color: var(--sp-sys-color-surface);
      position: sticky;
      top: 0;
    }

    tbody tr {
      vertical-align: baseline;

      &:last-child td {
        border-bottom: none;
      }

      &:nth-child(even) {
        background-color: var(--sp-sys-color-surface-variant);
      }
    }
  }

  &__select {
    width: var(--sp-ref-scale-32);
    text-align: center;
  }

  &__status {
    width: 2rem;
    text-align: center;
  }

  &__changes {
    display: flex;
    align-items: baseline;
    gap: var(--sp-ref-scale-4);
  }

  &__footer {
    position: sticky;
    bottom: 0;
    background-color: var(--sp-sys-color-surface);
    border-top: var(--sp-sys-border-width-default) solid var(--sp-sys-color-outline-variant);
  }

  // Screen-reader-only text for the status column's assistive label.
  &__sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }
}
</style>
